import { Button } from "@/components/ui/button";
import IconProvider from "@/components/ui/IconProvider";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface CompleteExportSectionProps {
  onExport: () => void;
  isExporting: boolean;
  exportProgress: string;
}

export function CompleteExportSection({ 
  onExport, 
  isExporting, 
  exportProgress 
}: CompleteExportSectionProps) {
  const { t } = usePlatformTranslation();

  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <div className="flex items-start gap-4">
        <IconProvider iconstring="fa-download" size={12} className="text-primary" />
        <div className="flex-1">
          <h2 className="heading-4">Complete Export</h2>
          <p className="text-muted-foreground mb-4">
            Export all game data including objects, templates, texts, documents, and their relationships. 
            This creates a comprehensive JSON file ready to be integrated into your game engine.
          </p>
          <div className="space-y-2 mb-4 text-sm text-muted-foreground">
            <p><strong>{t('platform.settings.importexportsection.includes')}</strong></p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>{t('platform.settings.importexportsection.all_objects_glossary_items_with_their_data_and_metadata')}</li>
              <li>{t('platform.settings.importexportsection.all_templates_blueprints_with_field_definitions')}</li>
              <li>{t('platform.settings.importexportsection.all_text_strings_with_translations')}</li>
              <li>{t('platform.settings.importexportsection.all_documents_with_their_content')}</li>
              <li>{t('platform.settings.importexportsection.entity_relationships_showing_how_items_are_linked')}</li>
              <li>{t('platform.settings.importexportsection.tags_and_type_definitions')}</li>
            </ul>
          </div>
          <Button
            onClick={onExport}
            disabled={isExporting}
            size="lg"
            className="gap-2"
          >
            {isExporting ? (
              <>
                <IconProvider iconstring="fa-spinner" className="animate-spin" />
                {exportProgress || "Exporting..."}
              </>
            ) : (
              <>
                <IconProvider iconstring="fa-download" size={16} className="text-primary" />
                Export Complete Package
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
